////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.3                                                            //
//  Summary: Transparent site header — wordmark + burger only (1.0.3.md §3) //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////
//
//  Replaces the 1.0.1 bordered/opaque header wholesale. Fixed + fully
//  transparent so the hero's background reads edge-to-edge underneath it.
//
//  1.0.3 POST-REVIEW TWEAK (11/08/2026, tweak #1): the scroll-aware blurred
//  "pill" backdrop on the wordmark/burger has been removed wholesale — Mike's
//  call was that it fought the "transparent nav" brief rather than serving it.
//  In its place, the header's visible content (wordmark + burger, nothing else
//  lives here) fades to 20% opacity once scrolled past the threshold below, and
//  restores to full opacity on hover OR when focus lands anywhere inside it
//  (`focus-within`) — a keyboard user tabbing into the open menu panel (itself a
//  descendant of this row, see MenuDropdown below) must never be left
//  interacting with a near-invisible control, hence `focus-within` rather than
//  relying on hover alone.
//
//  ROUND-2 FIX (12/08/2026): the fade/hover classes and the `onMouseEnter`/
//  `onMouseLeave` menu triggers below live on the INNER content row (`max-w-6xl`,
//  centred), NOT the outer `<header>`. The outer element is `fixed inset-x-0
//  top-0` — full VIEWPORT width — so on any screen wider than the content's
//  max-width, that used to make the fade/hover trigger area include the
//  transparent dead space either side of the wordmark/burger, all the way to
//  the browser edges. In production this meant the header read as permanently
//  un-faded: `gui/debug/landing_topnav_logo_position_scroll50percent.png` shows
//  it at full opacity well past the fade threshold, because the header's own
//  hoverable/focusable box still covered that dead space — this was never a
//  broken `useScrolled`/Tailwind-class-generation bug (both verified working
//  in isolation), it was the hover TARGET being far larger than the visible
//  control it was meant to gate. Scoping both the CSS `:hover`/`:focus-within`
//  restore and the JS hover-opens-menu handlers to the same snug inner row
//  fixes both symptoms at once without touching MenuDropdown's own full-width
//  positioning (it is still a child of this same `relative` row, unchanged).
//
//  MARK — the fade threshold is a fixed 480px rather than a per-page hero
//  height (Home's hero is full-viewport per §4; every other page's is "≈60vh"
//  per §4's closing note, and Header has no route-awareness of which). 480px
//  comfortably clears a 60vh hero on the smallest supported viewport
//  (~800px tall) while still triggering well before a full-viewport hero ends
//  on larger screens. Flagging as a tuned approximation, not a design-file
//  value, same convention as typography.css's h1-huge clamp() comment.
//
////////////////////////////////////////////////////////////////////////////////

import { useId, useRef, useState } from 'react';
import { Link } from 'react-router';
import { Menu, X } from 'lucide-react';

import { useConfig } from '@/config';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { useScrolled } from '@/hooks/useScrolled';
import { DURATION, EASING } from '@/lib/motion';
import { MenuDropdown } from './MenuDropdown';

const HEADER_FADE_SCROLL_THRESHOLD_PX = 480;

// The header's opacity fade is a plain CSS `opacity` transition, not a
// framer-motion `Variants` object, so it cannot be one of lib/motion.ts's
// exports (same reasoning the 1.0.3 pill transition below it used to carry).
// Two literals — full-motion and reduced-motion — because this component has
// no framer-motion `reduceVariants()` to route a bare CSS transition through;
// `DURATION.fast`/`DURATION.reduced`/`EASING` are asserted against both
// immediately below, so a change to lib/motion.ts's single source of truth
// fails this file's typecheck/test rather than silently drifting. The literals
// MUST stay static strings — Tailwind v4 finds utility classes by scanning
// source text, so an interpolated/computed class name would silently never be
// generated.
const HEADER_FADE_TRANSITION = 'transition-opacity duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]';
const HEADER_FADE_TRANSITION_REDUCED = 'transition-opacity duration-150 ease-[cubic-bezier(0.22,1,0.36,1)]';

// MARK — asserted, not just commented: fails loudly (a thrown error at module
// load, caught by any test/story that imports this file) if lib/motion.ts's
// constants ever drift from the literal Tailwind classes above, rather than
// leaving that drift to be spotted by eye in a future review.
if (DURATION.fast !== 0.3 || DURATION.reduced !== 0.15 || EASING.join(',') !== '0.22,1,0.36,1') {
  throw new Error('Header.tsx\'s HEADER_FADE_TRANSITION literals have drifted from lib/motion.ts\'s DURATION/EASING — update both.');
}

export function Header () {
  const [menuOpen, setMenuOpen] = useState(false);
  // Whether the CURRENT open was an explicit activation (click/Enter/Space) as
  // opposed to the desktop hover affordance. Drives MenuDropdown's focus-move and
  // Tab trap, and whether closing needs to hand focus back to the trigger.
  const [openedByIntent, setOpenedByIntent] = useState(false);
  const { cfg } = useConfig();
  const reducedMotion = useReducedMotion();
  const scrolled = useScrolled(HEADER_FADE_SCROLL_THRESHOLD_PX);
  const menuId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const headerRef = useRef<HTMLElement>(null);

  // Non-touch devices additionally open on hover (§3: "Hover (desktop) or tap
  // (touch) drops the menu") — gated on `!touch` so a tap on a touchscreen,
  // which fires a synthetic `mouseenter` before `click`, cannot open-then-toggle
  // the panel shut in the same gesture. Click/tap (`onClick` below) works
  // everywhere regardless, so keyboard and touch users are never dependent on
  // hover at all.
  const hoverCapable = !cfg.client.capabilities.touch;

  // Content-row opacity fade (tweak #1, rescoped round-2 — see this file's own
  // MARK above) — `hover:opacity-100`/`focus-within:opacity-100` below always
  // win over the scrolled base class regardless of class order (Tailwind emits
  // variant utilities after plain ones; the pseudo-class selector is also
  // higher specificity than the plain one either way), so hover or any focus
  // inside this row — including the open MenuDropdown, a descendant of it —
  // restores full opacity even while `scrolled` is true.
  const fadeTransition = reducedMotion ? HEADER_FADE_TRANSITION_REDUCED : HEADER_FADE_TRANSITION;
  const fadeOpacityClass = scrolled ? 'opacity-20' : 'opacity-100';

  // Returns focus to the burger only when focus is actually inside the header —
  // i.e. the panel really did hold it. Closing a merely-hovered panel must not
  // yank the caret out of whatever the visitor was typing in further down the
  // page, and letting a focused panel unmount without this drops focus to <body>
  // (losing the keyboard user's place entirely).
  const closeMenu = () => {
    const hadFocus = headerRef.current?.contains(document.activeElement) ?? false;
    setMenuOpen(false);
    setOpenedByIntent(false);
    if (hadFocus) triggerRef.current?.focus();
  };

  return (
    // Positioning/stacking only — the fade/hover behaviour below lives on the
    // inner content row, not here (see this file's ROUND-2 FIX MARK).
    <header ref={headerRef} className="fixed inset-x-0 top-0 z-50">
      <div
        className={`relative mx-auto flex max-w-6xl items-center justify-between px-6 py-4 hover:opacity-100 focus-within:opacity-100 ${fadeTransition} ${fadeOpacityClass}`}
        onMouseEnter={hoverCapable ? () => setMenuOpen(true) : undefined}
        onMouseLeave={hoverCapable ? closeMenu : undefined}
      >
        <Link to="/" aria-label="IRONSIDE home" className="rounded-full px-3 py-2">
          <img src={cfg.assets.logo.hoz} alt="IRONSIDE" className="h-8" />
        </Link>

        <button
          ref={triggerRef}
          type="button"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          // MARK — `aria-haspopup` deliberately NOT set. It was `="true"`, which is
          // an alias for `="menu"` and promises the panel exposes menu semantics
          // (role="menu" + role="menuitem" children with arrow-key navigation).
          // The panel is a <nav> of ordinary links, so that promise was false and
          // left AT users hunting for a keyboard model that does not exist.
          // `aria-expanded` + `aria-controls` fully describe a disclosure.
          aria-expanded={menuOpen}
          aria-controls={menuId}
          onClick={() => {
            if (menuOpen) {
              closeMenu();
            } else {
              setOpenedByIntent(true);
              setMenuOpen(true);
            }
          }}
          className="rounded-full p-2 text-ink"
        >
          {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </button>

        <MenuDropdown id={menuId} open={menuOpen} onClose={closeMenu} focusOnOpen={openedByIntent} />
      </div>
    </header>
  );
}
